//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import React, { useCallback, useState, useEffect } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';

import { getCodeMirrorOptions } from 'utils/CodeMirrorUtil';

// Import CodeMirror CSS and modes
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/python/python';
import 'codemirror/mode/javascript/javascript';

import './CodeEditor.less';

export interface CodeEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  language?: 'python' | 'javascript' | 'json' | 'text';
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  value = '',
  onChange,
  language = 'python',
  placeholder,
  className = '',
  disabled = false,
  size = 'medium',
  ...restProps
}) => {
  // Support both value and defaultValue props
  const initialValue = (restProps as any).defaultValue || value || '';
  const [editorValue, setEditorValue] = useState(initialValue);

  // Update local state when prop value changes
  useEffect(() => {
    const newValue = (restProps as any).defaultValue || value || '';
    setEditorValue(newValue);
  }, [value, (restProps as any).defaultValue]);

  const getOptions = useCallback(() => {
    const baseOptions = getCodeMirrorOptions(disabled);
    return {
      ...baseOptions,
      mode: language === 'text' ? undefined : language === 'json' ? 'javascript' : language,
      theme: 'default',
      indentUnit: language === 'python' ? 4 : 2,
      tabSize: language === 'python' ? 4 : 2,
      placeholder: placeholder,
      dragDrop: false,
    };
  }, [language, disabled, placeholder]);

  const handleChange = useCallback(
    (editor: CodeMirror.Editor, data: any, newValue: string) => {
      if (!disabled) {
        setEditorValue(newValue);
        onChange?.(newValue);
      }
    },
    [onChange, disabled]
  );

  return (
    <div className={`synri-code-editor synri-code-editor--${size} ${className}`}>
      <CodeMirror
        className="synri-code-editor__container"
        value={editorValue}
        options={getOptions()}
        onBeforeChange={handleChange}
      />
    </div>
  );
};

export default CodeEditor;
